// src/design-system/Organisms/CheckoutPaymentDetailsSection/index.tsx

import React, { useState, useEffect, useCallback, memo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, FloatingLabelInput } from '@/design-system/Atoms';
import { useCheckoutService } from '@/rtkQuery/features/hooks/useCheckoutService';

import {
    formatCardNumber,
    getCardType,
    getCardIcon,
    getCVVLength,
    formatExpirationDate
} from '@/utils/cardUtils';
import { paymentFormSchema, type PaymentFormSchema } from '@/validation-schemas/payment-form-schema';

interface CheckoutPaymentDetailsSectionProps {
    onComplete: (paymentData: any) => void;
}

const CardNumberInput = memo(({ field, fieldState, cardType, getCardIcon }: any) => {
    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        const rawValue = inputValue.replace(/\D/g, '');
        const limitedValue = rawValue.slice(0, 19);
        field.onChange(limitedValue);
    }, [field]);

    return (
        <div className="relative">
            <FloatingLabelInput
                id="cardNumber"
                label="Card Number"
                type="text"
                value={formatCardNumber(field.value)}
                onChange={handleChange}
                onBlur={field.onBlur}
                ref={field.ref}
                maxLength={23}
                placeholder="1234 5678 9012 3456"
                error={fieldState.error?.message}
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-2xl">
                {getCardIcon(cardType)}
            </div>
            {cardType !== 'unknown' && (
                <div className="absolute right-12 top-1/2 transform -translate-y-1/2 text-xs text-gray-500 capitalize">
                    {cardType}
                </div>
            )}
        </div>
    );
});

const CVVInput = memo(({ field, fieldState, cardType, getCVVLength }: any) => {
    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value.replace(/\D/g, '');
        const maxLength = getCVVLength(cardType);
        field.onChange(rawValue.slice(0, maxLength));
    }, [field, cardType, getCVVLength]);

    return (
        <FloatingLabelInput
            id="ccv"
            label={`CVV ${cardType === 'amex' ? '(4 digits)' : '(3 digits)'}`}
            type="text"
            value={field.value}
            onChange={handleChange}
            onBlur={field.onBlur}
            ref={field.ref}
            maxLength={getCVVLength(cardType)}
            placeholder={cardType === 'amex' ? '1234' : '123'}
            error={fieldState.error?.message}
        />
    );
});

const MonthInput = memo(({ field, fieldState }: any) => {
    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value.replace(/\D/g, '');

        if (rawValue === '') {
            field.onChange('');
            return;
        }

        const limitedValue = rawValue.slice(0, 2);

        if (limitedValue.length === 1) {
            const digit = parseInt(limitedValue);
            if (digit >= 1 && digit <= 9) {
                field.onChange(limitedValue);
            } else {
                field.onChange('');
            }
        } else if (limitedValue.length === 2) {
            const month = parseInt(limitedValue);
            if (month >= 1 && month <= 12) {
                field.onChange(limitedValue);
            } else {
                field.onChange(limitedValue.charAt(0));
            }
        }
    }, [field]);

    const handleBlur = useCallback((_e: React.FocusEvent<HTMLInputElement>) => {
        if (field.value.length === 1) {
            const digit = parseInt(field.value);
            if (digit >= 1 && digit <= 9) {
                field.onChange(field.value.padStart(2, '0'));
            }
        }
        field.onBlur();
    }, [field]);

    return (
        <FloatingLabelInput
            id="expirationMonth"
            label="Expiration Month"
            type="text"
            value={field.value}
            onChange={handleChange}
            onBlur={handleBlur}
            ref={field.ref}
            maxLength={2}
            placeholder="MM"
            error={fieldState.error?.message}
        />
    );
});

const YearInput = memo(({ field, fieldState }: any) => {
    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value.replace(/\D/g, '');
        field.onChange(rawValue.slice(0, 4));
    }, [field]);

    return (
        <FloatingLabelInput
            id="expirationYear"
            label="Expiration Year"
            type="text"
            value={field.value}
            onChange={handleChange}
            onBlur={field.onBlur}
            ref={field.ref}
            maxLength={4}
            placeholder="YYYY"
            error={fieldState.error?.message}
        />
    );
});

const CheckoutPaymentDetailsSection: React.FC<CheckoutPaymentDetailsSectionProps> = ({
    onComplete
}) => {
    const [cardType, setCardType] = useState<string>('unknown');
    const [isFormValid, setIsFormValid] = useState(false);
    const [initialCardHash] = useState<string | null>(null);

    const {
        submitPaymentInformation,
        paymentLoading,
        paymentSuccess,
        paymentError,
        paymentErrorMessage,
        paymentData,
        savedCardData,
        savedCardLoading,
    } = useCheckoutService();

    const {
        register,
        handleSubmit,
        control,
        watch,
        formState: { errors, isSubmitting, isValid },
        getValues,
    } = useForm<PaymentFormSchema>({
        resolver: zodResolver(paymentFormSchema),
        mode: 'onChange',
        defaultValues: {
            cardHolderName: '',
            cardNumber: '',
            expirationMonth: '',
            expirationYear: '',
            ccv: '',
        },
    });

    const watchedCardNumber = watch('cardNumber');
    const watchedMonth = watch('expirationMonth');
    const watchedYear = watch('expirationYear');

    useEffect(() => {
        if (watchedCardNumber) {
            const type = getCardType(watchedCardNumber);
            setCardType(type);
        } else {
            setCardType('unknown');
        }
    }, [watchedCardNumber]);

    useEffect(() => {
        setIsFormValid(isValid);
    }, [isValid]);

    useEffect(() => {
        if (paymentSuccess && paymentData) {
            onComplete({
                paymentMethod: 'Credit Card',
                cardNumber: watchedCardNumber.replace(/\D/g, '').slice(-4),
                cardHolderName: watch('cardHolderName'),
                expiryDate: formatExpirationDate(watchedMonth, watchedYear),
                status: 'Success',
            });
        }
    }, [paymentSuccess, paymentData, onComplete, watchedCardNumber, watchedMonth, watchedYear, watch]);
    const isFormPristine = useCallback(() => {
        if (!savedCardData || !initialCardHash) return false;

        const values = getValues();

        return (
            values.cardHolderName === (savedCardData.CardHolderName || '') &&
            values.cardNumber === (savedCardData.CardNumber || '') &&
            values.expirationMonth === String(savedCardData.ExpMonth).padStart(2, '0') &&
            values.expirationYear === String(savedCardData.ExpYear)
        );
    }, [getValues, savedCardData, initialCardHash]);

    const onSubmit = useCallback(async (data: PaymentFormSchema) => {
        if (initialCardHash && isFormPristine()) {
            onComplete({
                paymentMethod: 'Credit Card',
                cardNumber: savedCardData?.LastFour,
                cardHolderName: data.cardHolderName,
                expiryDate: formatExpirationDate(data.expirationMonth, data.expirationYear),
                status: 'Success',
            });
            return;
        }

        try {
            const expirationDate = new Date(
                Number(data.expirationYear),
                Number(data.expirationMonth) - 1,
                1
            ).toISOString();

            await submitPaymentInformation({
                creditCardInformation: {
                    cardHolderName: data.cardHolderName,
                    cardNumber: data.cardNumber.replace(/\D/g, ''),
                    expirationDate,
                    ccv: data.ccv,
                },
            }).unwrap();

            onComplete({
                paymentMethod: 'Credit Card',
                cardNumber: data.cardNumber.replace(/\D/g, '').slice(-4),
                cardHolderName: data.cardHolderName,
                expiryDate: formatExpirationDate(data.expirationMonth, data.expirationYear),
                status: paymentSuccess ? 'Success' : 'Failure',
            });
        } catch (err) {
            console.error('Payment submission failed:', err);
        }
    }, [submitPaymentInformation, onComplete, paymentSuccess, initialCardHash, isFormPristine, savedCardData]);


    const onContinueToReview = useCallback(async () => {
        try {
            onComplete({
                status: paymentSuccess ? 'Success' : 'Failure',
            });
        } catch (err) {
            console.error('Payment submission failed:', err);
        }
    }, [onComplete, paymentSuccess]);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="form-bg bg-[#f6f7f9] mb-6 p-8 rounded-[20px]">
                <div className="flex items-center justify-between mb-5">
                    <h3 className="text-2xl text-[#181a20]">Credit Card</h3>
                    {cardType !== 'unknown' && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <span>{getCardIcon(cardType)}</span>
                            <span className="capitalize">{cardType}</span>
                        </div>
                    )}
                </div>

                {/* {savedCardData && (
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center gap-2 text-sm text-blue-700">
                            <span>💾</span>
                            <span>Using saved card information</span>
                        </div>
                    </div>
                )} */}

                {paymentError && (
                    <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex items-center gap-2 text-sm text-red-700">
                            <span>⚠️</span>
                            <span>{paymentErrorMessage}</span>
                        </div>
                    </div>
                )}

                <div className="flex flex-wrap">
                    <div className="w-full mb-6">
                        <FloatingLabelInput
                            id="cardHolderName"
                            label="Full Name"
                            type="text"
                            {...register('cardHolderName')}
                            error={errors.cardHolderName?.message}
                            placeholder="John Doe"
                        />
                    </div>

                    <div className="w-full mb-6">
                        <Controller
                            name="cardNumber"
                            control={control}
                            render={({ field, fieldState }) => (
                                <CardNumberInput
                                    field={field}
                                    fieldState={fieldState}
                                    cardType={cardType}
                                    getCardIcon={getCardIcon}
                                />
                            )}
                        />
                    </div>
                </div>

                <div className="flex flex-wrap mb-6 gap-y-6 gap-x-6">
                    <div className="w-full lg:w-[calc(33.333%-1rem)]">
                        <Controller
                            name="expirationMonth"
                            control={control}
                            render={({ field, fieldState }) => (
                                <MonthInput field={field} fieldState={fieldState} />
                            )}
                        />
                    </div>

                    <div className="w-full lg:w-[calc(33.333%-1rem)]">
                        <Controller
                            name="expirationYear"
                            control={control}
                            render={({ field, fieldState }) => (
                                <YearInput field={field} fieldState={fieldState} />
                            )}
                        />
                    </div>

                    <div className="w-full lg:w-[calc(33.333%-1rem)]">
                        <Controller
                            name="ccv"
                            control={control}
                            render={({ field, fieldState }) => (
                                <CVVInput
                                    field={field}
                                    fieldState={fieldState}
                                    cardType={cardType}
                                    getCVVLength={getCVVLength}
                                />
                            )}
                        />
                    </div>
                </div>

                {watchedMonth && watchedYear && (
                    <div className="mb-4 text-sm text-gray-600">
                        <span>Expires: {formatExpirationDate(watchedMonth, watchedYear)}</span>
                    </div>
                )}
                <div className="flex flex-col gap-4 md:flex-row items-center ">
                    <div className="flex-1 min-w-0">
                        <Button
                            type="submit"
                            variant="secondary"
                            size="xl"
                            loading={paymentLoading || isSubmitting || savedCardLoading}
                            disabled={(!isFormValid && !isFormPristine()) || paymentLoading || isSubmitting}
                            className="w-full"
                        >
                            {paymentLoading || isSubmitting ? 'Processing Payment...' : 'Submit Payment Information'}
                        </Button>
                    </div>
                    {savedCardData?.Hash && (
                        <div className="flex-1 min-w-0">
                            <Button
                                variant="graySubmitButton"
                                size="xl"
                                type='button'
                                className="w-full"
                                onClick={onContinueToReview}>
                                Continue to Review Order
                            </Button>
                        </div>
                    )}
                </div>


                {Object.keys(errors).length > 0 && (
                    <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                        <div className="text-sm text-red-700">
                            <div className="font-medium mb-1">Please correct the following errors:</div>
                            <ul className="list-disc list-inside space-y-1">
                                {Object.entries(errors).map(([field, error]) => (
                                    <li key={field}>{error?.message}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}
            </div>
        </form>
    );
};

export default CheckoutPaymentDetailsSection;